import { Link } from 'react-router';
import { ChevronRight, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';

const TONOS = {
  esmeralda: { caja: 'bg-marca-50 text-marca-600', cifra: 'text-armazon-900' },
  azul: { caja: 'bg-blue-50 text-blue-600', cifra: 'text-armazon-900' },
  ambar: { caja: 'bg-amber-50 text-amber-600', cifra: 'text-armazon-900' },
  rojo: { caja: 'bg-red-50 text-red-600', cifra: 'text-red-700' },
  violeta: { caja: 'bg-violet-50 text-violet-600', cifra: 'text-armazon-900' },
  pizarra: { caja: 'bg-armazon-100 text-armazon-500', cifra: 'text-armazon-900' },
} as const;

export type Tono = keyof typeof TONOS;

export function Cifra({
  icono: Icono,
  rotulo,
  valor,
  pie,
  variacion,
  tono = 'esmeralda',
  a,
}: {
  icono: LucideIcon;
  rotulo: string;
  valor: string;
  pie?: string;

  variacion?: number | null;
  tono?: Tono;
  a?: string;
}) {
  const { caja, cifra } = TONOS[tono];
  const subio = (variacion ?? 0) >= 0;
  const Flecha = subio ? TrendingUp : TrendingDown;

  const cuerpo = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className={`grid size-9 shrink-0 place-items-center rounded-xl ${caja}`}>
          <Icono className="size-[18px]" aria-hidden />
        </span>
        {a && (
          <ChevronRight
            className="size-4 shrink-0 text-armazon-300 transition group-hover:translate-x-0.5 group-hover:text-armazon-500"
            aria-hidden
          />
        )}
      </div>

      <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-armazon-500">
        {rotulo}
      </p>
      <p className={`mt-0.5 truncate text-2xl font-bold tabular-nums ${cifra}`}>{valor}</p>

      <div className="mt-1 flex items-center gap-1.5 text-[11px]">
        {variacion !== undefined && variacion !== null && (
          <span
            className={`inline-flex items-center gap-0.5 font-semibold ${
              subio ? 'text-marca-700' : 'text-red-700'
            }`}
          >
            <Flecha className="size-3" aria-hidden />
            {subio ? '+' : '−'}
            {Math.abs(variacion).toLocaleString('es-EC', { maximumFractionDigits: 1 })} %
          </span>
        )}
        {pie && <span className="truncate text-armazon-400">{pie}</span>}
      </div>
    </>
  );

  const clases =
    'group block rounded-2xl border border-armazon-100 bg-white p-4 shadow-sm transition hover:border-armazon-200 hover:shadow';

  return a ? (
    <Link to={a} className={clases}>
      {cuerpo}
    </Link>
  ) : (
    <article className={clases}>{cuerpo}</article>
  );
}

export interface Alerta {
  clave: string;
  titulo: string;
  detalle: string;
  gravedad: 'urgente' | 'atencion' | 'aviso';
  a: string;
}

const GRAVEDAD = {
  urgente: { caja: 'bg-red-50 text-red-600', texto: 'text-red-700' },
  atencion: { caja: 'bg-amber-50 text-amber-600', texto: 'text-amber-700' },
  aviso: { caja: 'bg-blue-50 text-blue-600', texto: 'text-blue-700' },
} as const;

export function Alertas({ alertas, icono }: { alertas: Alerta[]; icono: Record<string, LucideIcon> }) {
  if (alertas.length === 0) {
    return (
      <p className="p-6 text-center text-xs uppercase tracking-wide text-armazon-400">
        Nada pendiente de atender
      </p>
    );
  }

  return (
    <ul className="divide-y divide-armazon-100">
      {alertas.map((alerta) => {
        const Icono = icono[alerta.clave];
        const tono = GRAVEDAD[alerta.gravedad];

        return (
          <li key={alerta.clave}>
            <Link
              to={alerta.a}
              className="group flex items-center gap-3 px-2 py-3 transition hover:bg-armazon-50/60"
            >
              <span className={`grid size-9 shrink-0 place-items-center rounded-xl ${tono.caja}`}>
                {Icono && <Icono className="size-[18px]" aria-hidden />}
              </span>

              <span className="min-w-0 flex-1">
                <span className={`block text-xs font-semibold ${tono.texto}`}>{alerta.titulo}</span>
                <span className="block truncate text-[11px] text-armazon-500">
                  {alerta.detalle}
                </span>
              </span>

              <ChevronRight
                className="size-4 shrink-0 text-armazon-300 transition group-hover:translate-x-0.5 group-hover:text-armazon-500"
                aria-hidden
              />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
